import { InputText } from 'primereact/inputtext';
import type { KeyboardEvent } from 'react';

import { cn } from '@shared/lib/cn';

export interface BaseInputProps {
  id: string;
  value: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  name?: string;
  type?: 'text' | 'email' | 'tel' | 'search' | 'password';
  /** Mobile keyboard hint (PINs and one-time codes use `numeric`). */
  inputMode?: 'text' | 'numeric' | 'email' | 'tel' | 'search' | 'url';
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  invalid?: boolean;
  /** Show exactly as typed (emails, URLs) instead of the firm-wide upper case. */
  nocaps?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  maxLength?: number;
  /** Id of a <datalist> with suggestions. */
  list?: string;
  className?: string;
  'aria-label'?: string;
}

export function BaseInput({
  id,
  value,
  onChange,
  onBlur,
  onKeyDown,
  name,
  type = 'text',
  inputMode,
  placeholder,
  disabled,
  readOnly,
  invalid,
  nocaps,
  autoComplete,
  autoFocus,
  maxLength,
  list,
  className,
  'aria-label': ariaLabel,
}: BaseInputProps) {
  return (
    <InputText
      id={id}
      name={name ?? id}
      type={type}
      inputMode={inputMode}
      value={value}
      onChange={(e) => {
        onChange?.(e.target.value);
      }}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      invalid={invalid}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      maxLength={maxLength}
      list={list}
      aria-label={ariaLabel}
      aria-invalid={invalid ? true : undefined}
      aria-describedby={invalid ? `${id}-error` : undefined}
      className={cn('w-full', nocaps && 'nocaps', className)}
    />
  );
}
